/** XeAgents.ai **/
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Users,
  CreditCard,
  TrendingUp,
  UserPlus,
  Building2,
  Globe,
  Calendar,
  Crown,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { formatDate } from "@/lib/date-format";

interface Organization {
  id: string;
  name: string;
  country: string;
  createdAt: string;
}

// Backend response for /api/organizations/me
interface OrganizationResponse {
  organization: Organization;
  role: string;
}

// Backend response structure for seat usage
interface SeatUsage {
  currentSeats: number;
  maxSeats: number;
  availableSeats: number;
  isAtLimit: boolean;
  planName: string;
  canAddMore: boolean;
  additionalSeatPrice?: string;
  additionalSeatPriceRazorpay?: string;
}

// Backend response for /api/organizations/seats/statistics
interface SeatStatisticsResponse {
  usage: SeatUsage;
  plan: {
    id: string;
    name: string;
    displayName: string;
    maxSeats: number;
  } | null;
  subscription: {
    id: string;
    planId: string;
    status: string;
  } | null;
  recommendations: {
    shouldUpgrade: boolean;
    canAddMembers: boolean;
    utilizationPercentage: number;
  };
}

// Transformed seat statistics for UI
interface SeatStatistics {
  usedSeats: number;
  totalSeats: number;
  percentageUsed: number;
  canAddMore: boolean;
  planName: string;
  planType: string;
}

interface Member {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  user: {
    name: string;
    email: string;
  };
}

export default function OrganizationOverview() {
  const { t } = useTranslation();

  // Fetch organization details
  const { data: orgResponse, isLoading: orgLoading } = useQuery<OrganizationResponse>({
    queryKey: ["/api/organizations/me"],
  });

  // Extract organization from nested response
  const organization = orgResponse?.organization;

  // Fetch seat statistics
  const { data: seatStatsResponse, isLoading: statsLoading } = useQuery<SeatStatisticsResponse>({
    queryKey: ["/api/organizations/seats/statistics"],
  });

  // Transform seat statistics to expected format
  const seatStats: SeatStatistics | undefined = seatStatsResponse?.usage ? {
    usedSeats: seatStatsResponse.usage.currentSeats,
    totalSeats: seatStatsResponse.usage.maxSeats,
    percentageUsed: seatStatsResponse.recommendations?.utilizationPercentage ?? 
      Math.round((seatStatsResponse.usage.currentSeats / seatStatsResponse.usage.maxSeats) * 100),
    canAddMore: seatStatsResponse.usage.canAddMore,
    planName: seatStatsResponse.plan?.displayName ?? seatStatsResponse.usage.planName ?? "Free",
    planType: seatStatsResponse.plan?.name ?? "free",
  } : undefined;

  // Fetch recent members
  const { data: members, isLoading: membersLoading } = useQuery<Member[]>({
    queryKey: ["/api/organizations/members"],
  });

  const isLoading = orgLoading || statsLoading || membersLoading;

  // Get recent members (last 5)
  const recentMembers = members?.slice(0, 5) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organization Overview</h1>
          <p className="text-muted-foreground mt-1">
            Manage your team and organization settings
          </p>
        </div>
        <Link href="/app/organization/members">
          <Button>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        </Link>
      </div>

      {/* Organization Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>
                {isLoading ? <Skeleton className="h-6 w-48" /> : organization?.name}
              </CardTitle>
              <CardDescription>Your organization details</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Country */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Globe className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Country</p>
                {isLoading ? (
                  <Skeleton className="h-4 w-20 mt-1" />
                ) : (
                  <p className="text-sm text-muted-foreground">{organization?.country}</p>
                )}
              </div>
            </div>

            {/* Created Date */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Created</p>
                {isLoading ? (
                  <Skeleton className="h-4 w-24 mt-1" />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {organization?.createdAt && formatDate(organization.createdAt, "MMM d, yyyy")}
                  </p>
                )}
              </div>
            </div>

            {/* Plan Type */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Crown className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Plan</p>
                {isLoading ? (
                  <Skeleton className="h-4 w-16 mt-1" />
                ) : (
                  <Badge variant={seatStats?.planType === "pro" ? "default" : "secondary"}>
                    {seatStats?.planName || "Free"}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Team Members */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <>
                <Skeleton className="h-8 w-20 mb-1" />
                <Skeleton className="h-4 w-32" />
              </>
            ) : (
              <>
                <div className="text-2xl font-bold">{seatStats?.usedSeats || 0}</div>
                <p className="text-xs text-muted-foreground">
                  of {seatStats?.totalSeats || 0} seats used
                </p>
                {/* Progress Bar */}
                <div className="mt-3">
                  <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{
                        width: `${Math.min((seatStats?.percentageUsed || 0), 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Seat Availability */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Seat Availability</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <>
                <Skeleton className="h-8 w-20 mb-1" />
                <Skeleton className="h-4 w-32" />
              </>
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {(seatStats?.totalSeats || 0) - (seatStats?.usedSeats || 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {seatStats?.canAddMore ? "Available seats" : "No seats available"}
                </p>
                {!seatStats?.canAddMore && (
                  <Link href="/app/organization/billing">
                    <Button variant="ghost" size="sm" className="px-0 mt-2">
                      Purchase more seats
                    </Button>
                  </Link>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="space-y-2">
            <Link href="/app/organization/members">
              <Button variant="outline" size="sm" className="w-full justify-start">
                <Users className="h-4 w-4 mr-2" />
                Manage Team
              </Button>
            </Link>
            <Link href="/app/organization/billing">
              <Button variant="outline" size="sm" className="w-full justify-start">
                <CreditCard className="h-4 w-4 mr-2" />
                Billing & Seats
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Members */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Team Members</CardTitle>
              <CardDescription>Latest members to join your organization</CardDescription>
            </div>
            <Link href="/app/organization/members">
              <Button variant="outline" size="sm">
                View All
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-1" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          ) : recentMembers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No team members yet</p>
              <Link href="/app/organization/members">
                <Button variant="ghost" size="sm" className="mt-2">
                  Invite your first member
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {member.user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{member.user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{member.user.email}</p>
                  </div>
                  <Badge variant={member.role === "owner" ? "default" : "secondary"}>
                    {member.role}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}